import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import { ChatTurn } from "@/components/chat/ChatTurn";
import { SectionHead } from "@/components/editorial/SectionHead";
import { useArtifactDrawer } from "@/components/layout/artifactDrawerContext";
import { getSessionTurns } from "@/services/api/sessions";

export function SessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ["session-turns", sessionId],
    queryFn: () => getSessionTurns(sessionId!, 200),
    enabled: !!sessionId,
  });

  // Artifacts live in the shell drawer rather than inline, so a historic
  // transcript has to publish them too — otherwise anything this session built
  // would be unreachable when reading it back.
  const { setArtifacts } = useArtifactDrawer();
  const turnArtifacts = useMemo(
    () => [...(data ?? [])].reverse().flatMap((t) => t.artifacts ?? []),
    [data],
  );
  useEffect(() => {
    setArtifacts(turnArtifacts);
  }, [turnArtifacts, setArtifacts]);

  return (
    <div className="space-y-10">
      <Link to="/history" className="smallcaps text-warmgray-400 hover:text-oxblood-500">
        ← Back to history
      </Link>
      <SectionHead label="Session" italicSuffix="full transcript" count={data?.length} />

      {isLoading ? (
        <p className="text-warmgray-400">Loading transcript…</p>
      ) : error ? (
        <p className="text-oxblood-500">Could not load: {(error as Error).message}</p>
      ) : !data?.length ? (
        <p className="font-serif italic text-warmgray-400">No turns recorded.</p>
      ) : (
        <div className="space-y-12">
          {data.map((turn, idx) => (
            <ChatTurn
              key={turn.turn_id}
              index={idx + 1}
              query={turn.query}
              answer={turn.answer_summary ?? ""}
              citations={turn.citations}
              artifacts={turn.artifacts}
              groundingScore={turn.grounding_score}
            />
          ))}
        </div>
      )}
    </div>
  );
}
